import { FunctionComponent, PropsWithChildren, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Fab,
    Fade,
    TextField,
    Typography,
} from '@mui/material';
import {
    BaseCondition,
    ContingentCondition,
    PriceCondition,
} from '@/models/conditionLink';
import { LoadingContainer } from '@/components/Layout/Container';
import { PriceConditionPreview } from './PriceConditionPreview';
import { BoldLabel } from '@starjack/shared-components';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import { ContingentConditionPreview } from '@/pages/rulesets/components/conditions/ContingentConditionPreview';
import { ConditionPreviewContainer } from '@/pages/rulesets/components/conditions/ConditionList';

const isOfTypeContingentCondition = (
    toBeDetermined: BaseCondition
): toBeDetermined is ContingentCondition => {
    return Object.prototype.hasOwnProperty.call(
        toBeDetermined,
        'maxTicketsPerStartDay'
    );
};

const sortConditions = (conditions: BaseCondition[]) =>
    conditions.sort((a, b) => a.sort - b.sort);

type ConditionPreviewList = {
    conditions: BaseCondition[];
    onCreateOrUpdate: (
        condition?: BaseCondition,
        options?: { insertAtIndex?: number }
    ) => void;
    onDelete: (condition: BaseCondition) => void;
    onPriorityChange: (
        conditions: BaseCondition[],
        condition: BaseCondition,
        priorityChange: 'increase' | 'decrease'
    ) => void;
    onBulkUpdateConditions: (conditions: BaseCondition[]) => void;
    onAddClick: (options?: { insertAtIndex?: number }) => void;
    isLoading?: boolean;
};

export const ConditionPreviewList: FunctionComponent<
    PropsWithChildren<ConditionPreviewList>
> = ({
    conditions,
    onCreateOrUpdate,
    onDelete,
    onPriorityChange,
    isLoading,
    onAddClick,
    onBulkUpdateConditions,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const conditionsSorted = useMemo(
        () => sortConditions([...conditions]),
        [conditions]
    );
    const [allConditions, setAllConditions] = useState(conditionsSorted);
    const conditionsFiltered = useMemo(() => {
        const conditionsSorted = sortConditions([...conditions]);

        if (!searchTerm) {
            return conditionsSorted;
        }

        const searchTermTerms = searchTerm.split(' ').filter(Boolean);

        return conditionsSorted.filter((conditionLink) =>
            searchTermTerms.every((term) =>
                conditionLink.name.toLowerCase().includes(term.toLowerCase())
            )
        );
    }, [conditionsSorted, searchTerm]);

    const [oldConditionProps, setOldConditionProps] =
        useState(conditionsSorted);

    const [isDragging, setDragging] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // change from outside
    if (oldConditionProps !== conditionsSorted) {
        setAllConditions(conditionsSorted);
        setOldConditionProps(conditionsSorted);
    }

    const handlePriorityChange = (condition, priorityChange) =>
        onPriorityChange(conditionsSorted, condition, priorityChange);

    const handleDragEnd = (event) => {
        const { active, over } = event;

        // over is null when dropped outside the list
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = allConditions.findIndex(
            (item) => item.id === active.id
        );
        const newIndex = allConditions.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(allConditions, oldIndex, newIndex);

        // Outside the state updater: updaters must be pure (StrictMode runs them twice)
        setAllConditions(newItems);
        onBulkUpdateConditions(newItems);
    };

    const conditionsToRender =
        searchTerm.length > 0 ? conditionsFiltered : allConditions;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <Box>
                <TextField
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        width: '400px',
                        mb: 2,
                    }}
                    label='Regel suchen'
                    variant='filled'
                />

                <Card
                    sx={{
                        position: 'relative',
                    }}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant='h6'>
                            Angelegte Themenbereiche
                        </Typography>

                        <Button
                            sx={{
                                ml: 'auto',
                            }}
                            onClick={() => onAddClick()}
                            variant='contained'
                        >
                            + neue Regel
                        </Button>
                    </CardContent>

                    <Divider
                        sx={{
                            width: '100%',
                        }}
                    />

                    <SortableContext
                        items={conditionsSorted.map((c) => c.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <motion.div
                            layout
                            transition={{
                                stiffness: 500,
                            }}
                        >
                            {conditionsToRender.map((condition, index) => {
                                const isLast =
                                    index === conditionsSorted.length - 1;
                                return (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                        }}
                                        key={condition.id}
                                    >
                                        {isOfTypeContingentCondition(
                                            condition
                                        ) ? (
                                            <ContingentConditionPreview
                                                condition={
                                                    condition as ContingentCondition
                                                }
                                                onCopy={(template) => {
                                                    onCreateOrUpdate(template, {
                                                        insertAtIndex:
                                                            index + 1,
                                                    });
                                                }}
                                                onUpdate={onCreateOrUpdate}
                                                onDelete={onDelete}
                                                onPriorityChange={
                                                    handlePriorityChange
                                                }
                                                disableDrag={
                                                    !!searchTerm.length
                                                }
                                                position={condition.sort + 1}
                                                isFirst={index === 0}
                                                isLast={
                                                    index ===
                                                    conditions.length - 1
                                                }
                                                onDragStart={() =>
                                                    setDragging(true)
                                                }
                                                onDragEnd={() =>
                                                    setDragging(false)
                                                }
                                            />
                                        ) : (
                                            <PriceConditionPreview
                                                condition={
                                                    condition as PriceCondition
                                                }
                                                onCopy={onCreateOrUpdate}
                                                onUpdate={onCreateOrUpdate}
                                                onDelete={onDelete}
                                                disableDrag={
                                                    !!searchTerm.length
                                                }
                                                onPriorityChange={
                                                    handlePriorityChange
                                                }
                                                position={index + 1}
                                                isFirst={index === 0}
                                                isLast={
                                                    index ===
                                                    conditions.length - 1
                                                }
                                            />
                                        )}

                                        {!isLast && (
                                            <>
                                                <Fade
                                                    in={
                                                        searchTerm.length ===
                                                            0 &&
                                                        !isDragging &&
                                                        !isLoading
                                                    }
                                                >
                                                    <Fab
                                                        aria-label='Bedingung einfügen'
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            transform:
                                                                'translate(25%, -50%)',
                                                            left: '75%',
                                                        }}
                                                        onClick={() => {
                                                            onAddClick({
                                                                insertAtIndex:
                                                                    index + 1,
                                                            });
                                                        }}
                                                        size='small'
                                                        color='primary'
                                                    >
                                                        <AddIcon />
                                                    </Fab>
                                                </Fade>

                                                <Divider
                                                    sx={{
                                                        width: '1ß0%',
                                                    }}
                                                />
                                            </>
                                        )}
                                    </Box>
                                );
                            })}
                        </motion.div>
                    </SortableContext>

                    {!conditionsSorted.length && (
                        <ConditionPreviewContainer
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            Es wurde noch kein Regelwerk erstellt. Neue
                            Regelwerke lassen sich unter &nbsp;
                            <BoldLabel>
                                &ldquo;+ Neues Regelwerk&rdquo;
                            </BoldLabel>
                            &nbsp; hinzufügen.
                        </ConditionPreviewContainer>
                    )}

                    <Fade in={isLoading}>
                        <div>
                            <LoadingContainer cover />
                        </div>
                    </Fade>
                </Card>
            </Box>
        </DndContext>
    );
};
